import clsx from 'clsx';
import { formatRM, type Product } from '@bazaar/shared';

export interface ProductTileProps {
  product: Product;
  qtyInCart: number;
  onAdd: () => void;
  onDecrement: () => void;
}

export function ProductTile({ product, qtyInCart, onAdd, onDecrement }: ProductTileProps) {
  const outOfStock = product.stockQty <= 0;
  const atMax = qtyInCart >= product.stockQty;
  const disabled = outOfStock || atMax;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={onAdd}
        disabled={disabled}
        aria-label={`Add ${product.name}, ${formatRM(product.priceCents)}${outOfStock ? ', out of stock' : ''}`}
        className={clsx(
          'w-full text-left bg-surface rounded-xl border border-border overflow-hidden shadow-sm flex flex-col',
          disabled && 'opacity-60',
        )}
      >
        <div className="relative aspect-square w-full bg-teal-dark flex items-center justify-center">
          {product.thumbUrl ? (
            <img src={product.thumbUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="text-white font-heading font-semibold text-lg px-2 text-center">{product.code}</span>
          )}

          {outOfStock ? (
            <span className="absolute top-2 left-2 rounded-full bg-gray-300 text-text text-sm font-heading font-bold px-2 py-1">
              Out
            </span>
          ) : product.lowStock ? (
            <span className="absolute top-2 left-2 rounded-full bg-orange text-ink text-sm font-heading font-bold px-2 py-1">
              Low: {product.stockQty}
            </span>
          ) : null}

          {qtyInCart > 0 && (
            // A pink badge on a pink placeholder/photo (e.g. a pink product
            // tile background) can otherwise disappear entirely. The white
            // ring plus a subtle shadow keeps it visible against any tile
            // background, pink included -- ink text on pink stays the
            // accessible pairing (never white on pink/orange).
            <span className="absolute top-2 right-2 min-w-7 h-7 px-1.5 rounded-full bg-pink text-ink text-sm font-heading font-semibold ring-2 ring-white shadow flex items-center justify-center">
              {qtyInCart}
            </span>
          )}
        </div>

        <div className="p-2 flex flex-col gap-0.5">
          <span className="font-body font-semibold text-base text-text line-clamp-2">{product.name}</span>
          <span className="font-body text-sm text-muted">{product.code}</span>
          <span className="font-heading font-semibold text-base text-ink">{formatRM(product.priceCents)}</span>
        </div>
      </button>

      {qtyInCart > 0 && (
        <button
          type="button"
          onClick={onDecrement}
          aria-label={`Remove one ${product.name} from cart`}
          className="absolute bottom-2 right-2 h-12 w-12 rounded-full bg-surface border-2 border-teal-dark text-teal-dark font-heading font-bold text-base flex items-center justify-center shadow z-10"
        >
          &minus;
        </button>
      )}
    </div>
  );
}
